import { TRAINING_PROGRAM_COPY } from '#app/routes/mocra+/_form+/components/training-programs/constants.ts'
import { type TrainingProgramEntry } from '#app/routes/mocra+/_form+/components/training-programs/schema.ts'
import {
	TRAINING_AUDIENCE_OPTIONS,
	TRAINING_DELIVERY_OPTIONS,
	TRAINING_FREQUENCY_OPTIONS,
} from '#app/routes/mocra+/_form+/intake-options.ts'

export type TrainingAudienceValue =
	(typeof TRAINING_AUDIENCE_OPTIONS)[number]['value']

export type TrainingDeliveryValue =
	(typeof TRAINING_DELIVERY_OPTIONS)[number]['value']

export type TrainingCadenceValue =
	(typeof TRAINING_FREQUENCY_OPTIONS)[number]['value']

export type TrainingProgramDraft = {
	audience: TrainingAudienceValue
	delivery: TrainingDeliveryValue
	cadence: TrainingCadenceValue
	platform: string
	tracksCompletion: boolean
}

export function createTrainingProgramDraft(
	overrides: Partial<TrainingProgramDraft> = {},
): TrainingProgramDraft {
	return {
		audience: TRAINING_AUDIENCE_OPTIONS[0]?.value ?? 'everyone',
		delivery: TRAINING_DELIVERY_OPTIONS[0]?.value ?? 'lms',
		cadence: TRAINING_FREQUENCY_OPTIONS[0]?.value ?? 'onboarding_annual',
		platform: '',
		tracksCompletion: true,
		...overrides,
	}
}

export function sanitizeTrainingProgramDraft(
	draft: TrainingProgramDraft,
): TrainingProgramDraft {
	const audience = isTrainingAudienceValue(draft.audience)
		? draft.audience
		: (TRAINING_AUDIENCE_OPTIONS[0]?.value ?? 'everyone')
	const delivery = isTrainingDeliveryValue(draft.delivery)
		? draft.delivery
		: (TRAINING_DELIVERY_OPTIONS[0]?.value ?? 'lms')
	const cadence = isTrainingCadenceValue(draft.cadence)
		? draft.cadence
		: (TRAINING_FREQUENCY_OPTIONS[0]?.value ?? 'onboarding_annual')
	return {
		audience,
		delivery,
		cadence,
		platform: draft.platform.trim(),
		tracksCompletion: Boolean(draft.tracksCompletion),
	}
}

export function collectTrainingProgramErrors(
	draft: TrainingProgramDraft,
): string[] {
	const sanitized = sanitizeTrainingProgramDraft(draft)
	const errors: string[] = []
	if (sanitized.platform.length === 0) {
		errors.push('Name the platform or tool used.')
	}
	return errors
}

export function trainingProgramCardTitle(draft: TrainingProgramDraft): string {
	const sanitized = sanitizeTrainingProgramDraft(draft)
	return resolveAudienceLabel(sanitized.audience)
}

export function formatTrainingProgramSummary(
	draft: TrainingProgramDraft,
): string {
	const sanitized = sanitizeTrainingProgramDraft(draft)
	const platform =
		sanitized.platform || TRAINING_PROGRAM_COPY.summary.platformMissing
	const deliveryLabel = resolveDeliveryLabel(sanitized.delivery)
	const cadenceLabel = resolveCadenceLabel(sanitized.cadence)
	const completion = sanitized.tracksCompletion
		? TRAINING_PROGRAM_COPY.summary.tracked
		: TRAINING_PROGRAM_COPY.summary.manual
	return `${platform} (${deliveryLabel}) — Cadence: ${cadenceLabel} — ${completion}`
}

export function resolveAudienceLabel(value: TrainingAudienceValue): string {
	return (
		TRAINING_AUDIENCE_OPTIONS.find((option) => option.value === value)?.label ??
		value
	)
}

export function resolveDeliveryLabel(value: TrainingDeliveryValue): string {
	return (
		TRAINING_DELIVERY_OPTIONS.find((option) => option.value === value)?.label ??
		value
	)
}

export function resolveCadenceLabel(value: TrainingCadenceValue): string {
	return (
		TRAINING_FREQUENCY_OPTIONS.find((option) => option.value === value)
			?.label ?? value
	)
}

export function isTrainingAudienceValue(
	value: unknown,
): value is TrainingAudienceValue {
	return TRAINING_AUDIENCE_OPTIONS.some((option) => option.value === value)
}

export function isTrainingDeliveryValue(
	value: unknown,
): value is TrainingDeliveryValue {
	return TRAINING_DELIVERY_OPTIONS.some((option) => option.value === value)
}

export function isTrainingCadenceValue(
	value: unknown,
): value is TrainingCadenceValue {
	return TRAINING_FREQUENCY_OPTIONS.some((option) => option.value === value)
}

export function trainingProgramDraftToEntry(
	draft: TrainingProgramDraft,
): TrainingProgramEntry {
	const sanitized = sanitizeTrainingProgramDraft(draft)
	return {
		audience: sanitized.audience as TrainingProgramEntry['audience'],
		delivery: sanitized.delivery as TrainingProgramEntry['delivery'],
		cadence: sanitized.cadence as TrainingProgramEntry['cadence'],
		platform: sanitized.platform,
		tracksCompletion: sanitized.tracksCompletion,
	}
}

export function trainingProgramEntryToDraft(
	entry: TrainingProgramEntry,
): TrainingProgramDraft {
	const audience = isTrainingAudienceValue(entry.audience)
		? entry.audience
		: undefined
	const delivery = isTrainingDeliveryValue(entry.delivery)
		? entry.delivery
		: undefined
	const cadence = isTrainingCadenceValue(entry.cadence)
		? entry.cadence
		: undefined
	return sanitizeTrainingProgramDraft(
		createTrainingProgramDraft({
			audience,
			delivery,
			cadence,
			platform: entry.platform,
			tracksCompletion: entry.tracksCompletion,
		}),
	)
}
